import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { EChartsOption, SeriesOption } from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ConfigService } from '../../shared-services/config.service';
import { auditTime, BehaviorSubject, debounceTime, distinctUntilChanged, filter, Subject, take, takeUntil } from 'rxjs';
import { GlobalService } from '../../shared-services/global.service';
import { DataErrorComponent } from "../../global-components/data-error/data-error.component";

@Component({
  selector: 'app-quad-bubble-chart',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxEchartsModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    DataErrorComponent
  ],
  templateUrl: './quad-bubble-chart.component.html',
  styleUrl: './quad-bubble-chart.component.scss'
})
export class QuadBubbleChartComponent implements OnInit {
  chartOption: EChartsOption = {};
  min = 0;
  max = 10;
  quadrant = 0;
  rawData: any;
  filteredData: any;
  config: any;
  quarterYear: any = "";
  quarter: any = "";
  tableData: any = {
    exceeds: [],
    meets: [],
    under: [],
  };
  @Output() bubbleClicked = new EventEmitter<void>();
  @Input() selectedMarket: any;
  @Input() selectedEngine: any;
  @Input() editable: boolean = false;
  @Input() data!: any;
  @Output() widgetClick = new EventEmitter<string>();
  selectedBubbleIndex: number | null = null;
  selectedDataIndex: number | null = null;
  productId: any;
  private lastSelectedBubblePoint: any | null = null;
  private destroy$ = new Subject<void>();
  private statusByName = new Map<string, 'exceeds' | 'meets' | 'under'>();
  private latestDrilldownRequestId = 0;
  private lastQuarterSelectionKey: string = '';

  filters: any;
  generating: boolean = false;
  errorMessage = "Generate Scenarios";
  scenarioPayload: any;

  constructor(private service: ConfigService,
    private cdr: ChangeDetectorRef,
    private filterState: GlobalService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    if (this.data.showYear) { this.getQuarter(); }
    this.service.getConfigData(this.data.configUrl).subscribe((resp: any) => {
      this.min = resp.xAxis.min;
      this.max = resp.xAxis.max;
      this.quadrant = this.max / 2;
      this.config = resp;
    });
  }

  private isImprovementTrackingPage(): boolean {
    return (
      this.router.url.includes('/impr-tracking-a-a') ||
      this.router.url.includes('/impr-tracking-a-e')
    );
  }

  private isProductMetricsPage(): boolean {
    return this.router.url.includes('/product-metrics');
  }

  private isHistoricalProductMetricsPage(): boolean {
    return this.router.url.includes('/historical-product-metrics');
  }
  getQuarter() {
    this.service.getQuadrant().subscribe((data: any) => {
      this.quarterYear = data.quarterYear;
      this.quarter = data.quarter;
    })
  }
  ngAfterViewInit() {
    if (this.data.generatedData) {
      this.generating = true;
      this.filterState.scenarioGeneration$
        .pipe(takeUntil(this.destroy$))
        .subscribe(scenarioPayload => {
          this.scenarioPayload = scenarioPayload;
          this.generating = true;
          this.errorMessage = "Generating...";
          this.getGeneratedData(scenarioPayload);
        });
    }
    else {
      this.filterState.filters$
        .pipe(
          // short delay to collect rapid emissions
          auditTime(1000),
          filter((filters) => filters !== null && filters !== undefined),
          takeUntil(this.destroy$)
        )
        .subscribe((filters) => {
          this.filters = { ...filters.filter };
          this.handleHistoricalQuarterChange(this.filters);
          this.getChartData(filters.filter);
        });
      this.service.simulatedData$
        .pipe(
          filter(data => data !== null),
          debounceTime(300),
          takeUntil(this.destroy$)
        )
        .subscribe((data: any) => {
          const scoreTree = this.applySimulatedScores(data);
          this.cdr.detectChanges();
          this.setSimulatedData(scoreTree, data);
        });
    }

    this.filterState.reset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedBubbleIndex = null;
        this.selectedDataIndex = null;
        this.lastSelectedBubblePoint = null;
        this.productId = null;
        this.service.setBubbleClicked('');

        // On simulate2, reset should clear generated bubble view and wait for fresh generation.
        if (this.data?.generatedData) {
          this.rawData = null;
          this.filteredData = null;
          this.chartOption = {};
          this.scenarioPayload = null;
          this.generating = true;
          this.errorMessage = 'Generate Scenarios';
          this.cdr.detectChanges();
        }
      });
  }

  applySimulatedScores(tree: any[]): any[] {
    return tree.map(node => {
      const newNode: any = {
        ...node,
        score: node.simulatedScore !== undefined ? +node.simulatedScore : node.score
      };
      delete newNode.simulatedScore;

      if (node.children?.length) {
        newNode.children = this.applySimulatedScores(node.children);
      }

      return newNode;
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  getChartData(filters?: any) {
    if (this.data.setExtrakey) {
      const baseExtraKeys = ((this.data.extraKeys ?? []) as any[]).filter(
        (item: any) => !Object.prototype.hasOwnProperty.call(item || {}, 'version')
      );
      const selectedVersion = filters?.version?.[0];
      this.data.extraKeys =
        selectedVersion !== undefined && selectedVersion !== null && selectedVersion !== ''
          ? [...baseExtraKeys, { version: selectedVersion }]
          : baseExtraKeys;
    }

    this.service.fetchData(this.data.apiUrl, this.data.functionName, filters, this.data).subscribe((data: any) => {
      const responseNode = this.isHistoricalProductMetricsPage()
        ? (
            data?.responseList?.[0]?.responseData ??
            data?.responseData ??
            data
          )
        : (
            data?.responseList?.[0]?.responseData?.[0] ??
            data?.responseData?.[0] ??
            data?.responseData ??
            data
          );
      const sourceNode = responseNode?.actual ?? responseNode;
      const historicalBubbleNode = this.isHistoricalProductMetricsPage()
        ? this.findQuarterPayload(sourceNode)
        : sourceNode;
      this.rawData = this.normalizeBubblePayload(historicalBubbleNode);
      if (this.data.setDrillDown) {
        this.productId = this.extractNames(this.rawData);
        this.setDrilldown(this.productId);
      }
      if (this.data.status) {
        this.applyStatusFilter()
      }
      else {
        this.generateChart(this.rawData);
        this.filteredData = { ...this.rawData };
      }

    });
  }

  private handleHistoricalQuarterChange(filters: any): void {
    if (!this.isHistoricalProductMetricsPage()) {
      return;
    }

    const currentQuarterKey = this.toFilterValues(filters?.quarter_year)
      .map((value) => String(value).trim())
      .filter((value) => !!value)
      .join('|');

    if (!this.lastQuarterSelectionKey) {
      this.lastQuarterSelectionKey = currentQuarterKey;
      return;
    }

    if (currentQuarterKey !== this.lastQuarterSelectionKey) {
      this.selectedBubbleIndex = null;
      this.selectedDataIndex = null;
      this.lastSelectedBubblePoint = null;
      this.productId = null;
      this.service.setBubbleClicked('');
    }

    this.lastQuarterSelectionKey = currentQuarterKey;
  }

  private normalizeBubblePayload(raw: any): any {
    const normalized: any = { q1: [], q2: [], q3: [], q4: [] };
    if (!raw || typeof raw !== 'object') {
      return normalized;
    }

    const directKeys = ['q1', 'q2', 'q3', 'q4'];
    const hasDirectQuarterKeys = directKeys.some((key) => Array.isArray(raw[key]));
    if (hasDirectQuarterKeys) {
      directKeys.forEach((key) => {
        normalized[key] = (raw[key] || []).map((point: any) => this.normalizeBubblePoint(point));
      });
      return normalized;
    }

    if (Array.isArray(raw)) {
      raw.forEach((point: any) => {
        let bucket = String(
          point?.quadrant || point?.quarter || point?.q || point?.bucket || ''
        ).toLowerCase();
        if (!directKeys.includes(bucket) && this.isHistoricalProductMetricsPage()) {
          bucket = this.resolveQuadrantBucket(point);
        }
        if (directKeys.includes(bucket)) {
          normalized[bucket].push(this.normalizeBubblePoint(point));
        }
      });
      return normalized;
    }

    return normalized;
  }

  private resolveQuadrantBucket(point: any): string {
    const normalizedPoint = this.normalizeBubblePoint(point);
    const x = Number(normalizedPoint?.value?.[0] ?? 0);
    const y = Number(normalizedPoint?.value?.[1] ?? 0);
    return this.getQuadrant(x, y);
  }

  private findQuarterPayload(node: any): any {
    if (!node || typeof node !== 'object') return node;

    const hasQuarterBuckets = (value: any): boolean =>
      !!value &&
      typeof value === 'object' &&
      ['q1', 'q2', 'q3', 'q4'].some((k) => Array.isArray((value as any)[k]));

    if (hasQuarterBuckets(node)) return node;

    if (Array.isArray(node)) {
      for (const item of node) {
        const found = this.findQuarterPayload(item);
        if (found && hasQuarterBuckets(found)) return found;
      }
      return node;
    }

    for (const key of Object.keys(node)) {
      const found = this.findQuarterPayload(node[key]);
      if (found && hasQuarterBuckets(found)) return found;
    }

    return node;
  }

  private normalizeBubblePoint(point: any): any {
    if (!point || typeof point !== 'object') return { value: [0, 0, 0], name: '' };
    const rawValue = Array.isArray(point.value)
      ? point.value
      : Array.isArray(point.values)
        ? point.values
        : [
            point.productFit ?? point.product_fit ?? point.x ?? point.xAxisValue ?? 0,
            point.financialSignificance ?? point.financial_significance ?? point.y ?? point.yAxisValue ?? 0,
            point.revenue ?? point.revenue_mn ?? point.size ?? point.z ?? 0,
          ];
    return {
      ...point,
      keyCompetitor: point.keyCompetitor ?? point.key_competitor ?? point.competitor ?? point.keycompetitor,
      marketShare: point.marketShare ?? point.market_share ?? point.marketshare,
      value: [
        Number(rawValue?.[0] ?? 0),
        Number(rawValue?.[1] ?? 0),
        Number(rawValue?.[2] ?? 0),
      ],
    };
  }

  getGeneratedData(filters?: any) {
    this.service.customApi(this.data.apiUrl, filters, this.data).subscribe((data: any) => {
      this.rawData = data.responseData[0];
      this.generateChart(this.rawData);
      this.filteredData = { ...this.rawData };
      this.generating = false;
    });
  }

  applyStatusFilter() {
    const isStatusAll = this.filters.status?.includes?.('All');
    // Case: All selected in status
  if (isStatusAll) {
  this.getStatusTable(this.rawData);   // build exceeds/meets/under lists

  this.statusByName.clear();
  this.tableData.exceeds.forEach((x: any) => this.statusByName.set(x.name, 'exceeds'));
  this.tableData.meets.forEach((x: any) => this.statusByName.set(x.name, 'meets'));
  this.tableData.under.forEach((x: any) => this.statusByName.set(x.name, 'under'));

  this.generateChart(this.rawData);
  localStorage.setItem('filteredData', JSON.stringify(this.rawData));
  return;
}


    // Prepare status filter names
    let selectedNames: string[] = [];
    if (this.filters.status && !isStatusAll) {
      this.getStatusTable(this.rawData);

      this.statusByName.clear();

this.tableData.exceeds.forEach((x: any) => this.statusByName.set(x.name, 'exceeds'));
this.tableData.meets.forEach((x: any) => this.statusByName.set(x.name, 'meets'));
this.tableData.under.forEach((x: any) => this.statusByName.set(x.name, 'under'));

      selectedNames = this.filters.status
        .flatMap((item: any) => this.tableData[item] || [])
        .map((entry: any) => entry.name);
    }

    this.filteredData = { ...this.rawData };

    // Apply status filtering on full data
    if (!isStatusAll) {
      for (const key in this.rawData) {
        this.filteredData[key] = this.rawData[key].filter((item: any) =>
          selectedNames.includes(item.name)
        );
      }
    }
    localStorage.setItem('filteredData', JSON.stringify(this.filteredData));
    this.generateChart(this.filteredData);
  }

  generateChart(data: any) {
    this.chartOption = {
      tooltip: {
        trigger: 'item',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        padding: 0,
        extraCssText: 'box-shadow: none;',
        formatter: (params: any) => {
          const competitorValue =
            params.data.keyCompetitor ??
            params.data.key_competitor ??
            params.data.competitor ??
            params.data.keycompetitor;
          const competitor = competitorValue
            ? `<div><strong>Key Competitor:</strong> ${competitorValue}</div>`
            : '';
          const value = params.data.marketShare ?? params.data.market_share ?? params.data.marketshare;

          const marketShare = (() => {
            const normalizedValue = value !== null && value !== undefined ? String(value).trim() : '';
            const numericValue = Number(normalizedValue);
            const isUnavailable =
              normalizedValue === '' ||
              ['na', 'n/a', 'null', 'undefined'].includes(normalizedValue.toLowerCase()) ||
              (!Number.isNaN(numericValue) && numericValue === 0);

            if (isUnavailable) {
              return '<div><strong>Market Share:</strong> NA</div>';
            }

            const suffix = !Number.isNaN(numericValue) ? '%' : ''; // add % only if numeric
            return `<div><strong>Market Share:</strong> ${normalizedValue}${suffix}</div>`;
          })();
          const revenueLabel = this.isProductMetricsPage() ? `${this.quarterYear} Revenue($M)` : 'Revenue($M)';
          const revenue =
            params.value[2] !== null && params.value[2] !== undefined
              ? `<div><strong>${revenueLabel}:</strong> ${params.value[2]}</div>`
              : '';
          return `
    <div style="padding: 8px 12px; border-radius: 8px; background: rgba(50, 50, 50); color: #fff; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);">
      <div style="font-size: 14px; font-weight: 600; margin-bottom: 6px; color: #FFD700;">
        ${params.name}
      </div>
      <div><strong>Product Fit:</strong> ${params.value[0]}</div>
      <div><strong>Financial Significance:</strong> ${params.value[1]}</div>
      ${revenue}
      ${competitor}
      ${marketShare}
    </div>
  `;
        }
      },

      grid: {
        left: '3%',
        right: '1%',
        top: '3%',
        bottom: '5%',
        containLabel: true,
      },

      legend: this.config.legend,
      xAxis: this.getAxisOptions('xAxis'),
      yAxis: this.getAxisOptions('yAxis'),
      // dataZoom: [
      //   {
      //     type: 'inside',
      //     xAxisIndex: [0],
      //     yAxisIndex: [0],
      //     zoomOnMouseWheel: true,
      //     moveOnMouseWheel: true,
      //     moveOnMouseMove: true
      //   },
      //   {
      //     type: 'slider',
      //     show: true,
      //     xAxisIndex: 0,
      //     start: 0,
      //     end: 100,
      //     filterMode: 'none'
      //   },
      //   {
      //     type: 'slider',
      //     show: true,
      //     yAxisIndex: 0,
      //     start: 0,
      //     end: 100,
      //     filterMode: 'none'
      //   }
      // ],
      dataZoom: [
        { type: 'inside', xAxisIndex: 0 },
        { type: 'inside', yAxisIndex: 0 },
      ],
      series: [
        this.getQuadrantAreaSeries(),
        ...this.buildSeries(data, this.config.bubbleColors),
        this.getDividerSeries(),
      ] as SeriesOption[],
      graphic: this.config.quadrantsLabelStyle,
    };
  }
  getStatusTable(data: any) {
    const tableData: any = {
      exceeds: [],
      meets: [],
      under: [],
    };
    for (const key in data) {
      data[key].map((point: any) => {
        const [x, y] = point.value;
        const actualQuadrant = this.getQuadrant(x, y);
        const expectedQuadrant = key;
        if (actualQuadrant === expectedQuadrant) {
          tableData.meets.push({ name: point.name, revenue: point.value[2] });
        } else {
          switch (expectedQuadrant) {
            case 'q1':
              if (['q2', 'q3', 'q4'].includes(actualQuadrant)) {
                tableData.exceeds.push({
                  name: point.name,
                  revenue: point.value[2],
                });
              }
              break;
            case 'q2':
              if (['q1', 'q3', 'q4'].includes(actualQuadrant)) {
                tableData.under.push({
                  name: point.name,
                  revenue: point.value[2],
                });
              }
              break;
            case 'q3':
              if (['q2', 'q4', 'q1'].includes(actualQuadrant)) {
                tableData.exceeds.push({
                  name: point.name,
                  revenue: point.value[2],
                });
              }
              break;
            case 'q4':
              if (actualQuadrant === 'q2') {
                tableData.exceeds.push({
                  name: point.name,
                  revenue: point.value[2],
                });
              } else if (['q1', 'q3'].includes(actualQuadrant)) {
                tableData.under.push({
                  name: point.name,
                  revenue: point.value[2],
                });
              }
              break;
          }
        }
      });
    }
    this.tableData = tableData;
    return this.tableData;
  }

  setSimulatedData(scoreTree: any, simultedData: any) {
    if (this.productId) {
      this.service
        .getSimulatedData(this.productId, scoreTree)
        .subscribe((data: any) => {
          const rawData = this.extractNonEmptyQuarter(data?.responseList?.[0]?.responseData?.[0]);
          this.generateChart(rawData);
          this.service
            .getSimulatedDrilldown(this.productId, scoreTree)
            .subscribe((resp: any) => {
              const combinedDrillDownData = this.replaceSimulatedScores(
                simultedData,
                resp?.responseList?.[0]?.responseData?.[0].simulation
              );
              this.service.setDrilldownData({ data: combinedDrillDownData });
            });
          this.cdr.detectChanges();
        });
    }
  }

  replaceSimulatedScores(base: any[], scores: any[]): any[] {
    const merge = (baseNodes: any[], scoreNodes: any[]) => {
      return baseNodes.map((baseNode) => {
        const scoreNode = scoreNodes.find((n) => n.name === baseNode.name);
        const updatedNode: any = { ...baseNode };

        if (scoreNode?.score !== undefined) {
          updatedNode.simulatedScore = scoreNode.score;
        }

        if (baseNode.children && scoreNode?.children) {
          updatedNode.children = merge(baseNode.children, scoreNode.children);
        }

        return updatedNode;
      });
    };

    return merge(base, scores);
  }

  extractNonEmptyQuarter(data: any): any[] {
    const result: any = {
      q1: [],
      q2: [],
      q3: [],
      q4: [],
    };
    data.simulation.name = 'S_' + data.simulation.name;
    for (const key in this.filteredData) {
      if (this.filteredData[key].length > 0) {
        result[key].push(data.actual);
        result[key].push(data.simulation);
        break;
      }
    }
    return result;
  }

private getStatusLabelColor(name: string): string {
  const key = this.statusByName.get(name);
  const configuredColor = this.config?.labelColor;
  const isTransparentColor =
    typeof configuredColor === 'string' &&
    (configuredColor.toLowerCase() === '#0000' || configuredColor.toLowerCase() === 'transparent');
  const fallbackColor =
    this.isImprovementTrackingPage() || !configuredColor || isTransparentColor
      ? '#111827'
      : configuredColor;

  switch (key) {
    case 'exceeds': return '#05714aff'; // green
    case 'meets':   return '#000000'; // blue
    case 'under':   return '#dc2626'; // red
    default:        return fallbackColor;
  }
}


  private buildSeries(data: any, colors: string[]) {
    const forceAlwaysVisibleLabels = this.isImprovementTrackingPage();

    return ['q1', 'q2', 'q3', 'q4'].map((key, index) => ({
      name: this.config.legend.data[index],
      type: 'scatter',
      clip: true,
      z: 10,
      selectedMode: 'single',
      emphasis: this.config.emphasis,
      select: this.config.select,
      label: this.getLabelOptions(),
      labelLayout: { hideOverlap: !forceAlwaysVisibleLabels, dy: 5 },
      itemStyle: {
        color: new echarts.graphic.RadialGradient(0.35, 0.3, 1, [
          { offset: 0, color: this.lightenColor(colors[index], 30) },
          { offset: 0.4, color: this.lightenColor(colors[index], 2) },
          { offset: 1, color: colors[index] },
        ]),
        shadowBlur: 25,
        shadowColor: 'rgba(0, 0, 0, 0.35)',
        shadowOffsetY: 10,
      },
      data: data[key]?.map((point: any) => {
        const normalizedPoint = this.normalizeBubblePoint(point);
        const [x, y] = normalizedPoint.value;
        const actualQuadrant = this.getQuadrant(x, y);
        const expectedQuadrant = key;

        // Build base display name
        let displayName = normalizedPoint.name;

        // Add date prefix if START/END periodType exists
        if (normalizedPoint.periodType === 'START' && this.filters.start_date?.[0]) {
          displayName = `${this.filters.start_date[0]} - ${normalizedPoint.name}`;
        } else if (normalizedPoint.periodType === 'END' && this.filters.end_date?.[0]) {
          displayName = `${this.filters.end_date[0]} - ${normalizedPoint.name}`;
        }

        // Append scenarioKey if present
        if (normalizedPoint.scenarioKey) {
          displayName = `${displayName} (${normalizedPoint.scenarioKey})`;
        }

        return {
          ...normalizedPoint,
          name: displayName, // final display name with scenarioKey appended
          itemStyle: this.getItemStyle(
            this.resolveColorFromSegmentCode(normalizedPoint, colors, colors[index]),
            normalizedPoint.name
          ),
          label: {
                color: this.getStatusLabelColor(normalizedPoint.name),
            },
          };
        }),
      symbolSize: (d: any) => this.normalizeSize(d[2], 10, 50, data) || 10,
    }));
  }

  private resolveColorFromSegmentCode(point: any, colors: string[], fallbackColor: string): string {
    const colorKey = this.getColorKeyFromSegmentCode(point?.segment_code);
    if (!colorKey) {
      return fallbackColor;
    }

    const keyIndexMap: Record<string, number> = { q1: 0, q2: 1, q3: 2, q4: 3 };
    const colorIndex = keyIndexMap[colorKey];
    const resolvedColor = colors?.[colorIndex];
    return resolvedColor || fallbackColor;
  }

  private getColorKeyFromSegmentCode(segmentCode: any): 'q1' | 'q2' | 'q3' | 'q4' | null {
    if (segmentCode === null || segmentCode === undefined) {
      return null;
    }

    const raw = String(segmentCode).trim().toLowerCase();
    if (!raw) {
      return null;
    }

    // normalize values like "CORE/NICHE", "core_niche", "core-niche"
    const compact = raw.replace(/[\s_\/-]/g, '');
    const segmentMap: Record<string, 'q1' | 'q2' | 'q3' | 'q4'> = {
      q1: 'q1',
      q2: 'q2',
      q3: 'q3',
      q4: 'q4',
      '1': 'q1',
      '2': 'q2',
      '3': 'q3',
      '4': 'q4',
      cautionzone: 'q1',
      growth: 'q2',
      harvest: 'q3',
      coreniche: 'q4',
      core: 'q4',
      niche: 'q4',
    };

    return segmentMap[compact] || null;
  }



  private getQuadrant(x: number, y: number): string {
    if (x > this.quadrant && y > this.quadrant) return 'q2';
    if (x <= this.quadrant && y > this.quadrant) return 'q1';
    if (x <= this.quadrant && y <= this.quadrant) return 'q3';
    if (x > this.quadrant && y <= this.quadrant) return 'q4';
    return '';
  }


  private getLabelOptions() {
    const configuredColor = this.config?.labelColor;
    const isTransparentColor =
      typeof configuredColor === 'string' &&
      (configuredColor.toLowerCase() === '#0000' || configuredColor.toLowerCase() === 'transparent');

    const labelColor =
      this.isImprovementTrackingPage() || !configuredColor || isTransparentColor
        ? '#111827'
        : configuredColor;

    return {
      show: true,
      position: this.config.labelposition,
      fontSize: this.config.labelSize,
      backgroundColor: this.config.labelBg,
      fontWeight: this.config.labelWeight,
      color: labelColor,
      formatter: (params: any) => {
        let name = params.name;
        const replacements: [RegExp, string][] = [
          [/Mining/gi, 'MIN'],
          [/Marine/gi, 'MX'],
          [/QSK/gi, 'Q'],
          [/OIL & GAS/gi, 'OG'],
          [/ GAS/gi, 'G'],
          [/O&G/gi, 'OG'],
          [/Defense/gi, ''],
          [/QST/gi, 'Q'],
          [/Rail/gi, 'Rail']
        ];
        for (const [pattern, replacement] of replacements) {
          name = name.replace(pattern, replacement);
        }
        return name;
      },
    };
  }

  private getItemStyle(color: string, name: string) {
    const isSpecialLabel = name?.startsWith('S_');
    const baseColor = isSpecialLabel ? this.lightenColor(color, 30) : color;

    return {
      color: new echarts.graphic.RadialGradient(0.35, 0.3, 1, [
        {
          offset: 0,
          color: this.lightenColor(baseColor, 30), // lighter center
        },
        {
          offset: 0.4,
          color: this.lightenColor(baseColor, 2), //  transition
        },
        {
          offset: 1,
          color: baseColor, // Original bubble color at edge
        },
      ]),
      shadowBlur: 25,
      shadowColor: 'rgba(0, 0, 0, 0.35)',
      shadowOffsetY: 10, // Stronger vertical drop for realism

      // color:baseColor,
      // borderColor: this.getBorderColor(actual, expected),
      // borderColor: '#000',
      // borderWidth: 1,
      // borderType: isSpecialLabel ? 'dotted' : 'solid',
    };
  }

  darkenColor(hex: string, percent: number): string {
    // Remove '#' if present
    hex = hex.replace('#', '');

    // Parse r,g,b
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Convert RGB to HSL
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }

      h /= 6;
    }

    // Darken by reducing lightness
    l = l * (1 - percent / 100);
    if (l < 0) l = 0;

    // Convert HSL back to RGB
    let rNew, gNew, bNew;

    if (s === 0) {
      rNew = gNew = bNew = l; // achromatic
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      rNew = hue2rgb(p, q, h + 1 / 3);
      gNew = hue2rgb(p, q, h);
      bNew = hue2rgb(p, q, h - 1 / 3);
    }

    // Convert RGB [0..1] to hex
    const toHex = (x: number) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(rNew)}${toHex(gNew)}${toHex(bNew)}`;
  }

  private lightenColor(hex: string, percent: number) {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  }

  getBorderColor(actual: string, expected: string): string {
    if (actual === expected) {
      return 'black'; // expected
    }

    let expectationStat = '';

    switch (expected) {
      case 'q1':
        if (['q2', 'q3', 'q4'].includes(actual)) {
          expectationStat = 'exceeds';
        }
        break;
      case 'q2':
        if (['q1', 'q3', 'q4'].includes(actual)) {
          expectationStat = 'under';
        }
        break;
      case 'q3':
        if (['q2', 'q4', 'q1'].includes(actual)) {
          expectationStat = 'exceeds';
        }
        break;
      case 'q4':
        if (actual === 'q2') {
          expectationStat = 'exceeds';
        } else if (['q1', 'q3'].includes(actual)) {
          expectationStat = 'under';
        }
        break;
    }

    switch (expectationStat) {
      case 'exceeds':
        return this.config.bubbleBorder.exceeds;
      case 'under':
        return this.config.bubbleBorder.under;
      case 'expected':
        return this.config.bubbleBorder.expected;
      default:
        return this.config.bubbleBorder.expected;
    }
  }
  extractNames(data: any) {
    let result: string = '';

    // Make data always an array
    const dataArray = Array.isArray(data) ? data : [data];

    dataArray.forEach((item: any) => {
      ['q1', 'q2', 'q3', 'q4'].forEach(q => {
        if (Array.isArray(item[q])) {
          item[q].forEach(obj => {
            if (obj.name) {
              result = obj.name;
            }
          });
        }
      });
    });

    return result;
  }


  private getAxisOptions(axisKey: 'xAxis' | 'yAxis') {
    const axis = this.config[axisKey];

    return {
      name: axis.name,
      min: this.min,
      max: this.max,
      nameLocation: axis.nameLocation,
      nameGap: axis.nameGap,
      nameTextStyle: axis.nameTextStyle,
      interval: this.quadrant,
      splitLine: { show: false },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontWeight: axis.axisLabel.fontWeight,
        fontSize: axis.axisLabel.fontSize,
        color: axis.axisLabel.color,
        ...(axisKey === 'yAxis' && {
          formatter: (value: number) => (value === this.min ? '' : value),
        }),
      },
    };
  }

  private getQuadrantAreaSeries() {
    const qs = this.config.quadrantsLabelStyle;
    return {
      type: 'scatter',
      data: [],
      markArea: {
        silent: true,
        data: [
          [
            { xAxis: this.min, yAxis: this.quadrant },
            {
              xAxis: this.quadrant,
              yAxis: this.max,
              itemStyle: { color: qs[0].background },
            },
          ],
          [
            { xAxis: this.quadrant, yAxis: this.quadrant },
            {
              xAxis: this.max,
              yAxis: this.max,
              itemStyle: { color: qs[1].background },
            },
          ],
          [
            { xAxis: this.min, yAxis: this.min },
            {
              xAxis: this.quadrant,
              yAxis: this.quadrant,
              itemStyle: { color: qs[2].background },
            },
          ],
          [
            { xAxis: this.quadrant, yAxis: this.min },
            {
              xAxis: this.max,
              yAxis: this.quadrant,
              itemStyle: { color: qs[3].background },
            },
          ],
        ],
      },
    };
  }

  private getDividerSeries() {
    return {
      name: 'Dividers',
      type: 'scatter',
      data: [[this.quadrant, this.quadrant]],
      z: 0,
      symbolSize: 1,
      itemStyle: { opacity: 0 },
      tooltip: { show: false },
      markLine: {
        z: 0,
        symbol: 'none',
        tooltip: { show: false },
        label: { show: false },
        lineStyle: { type: 'solid', color: '#808080', width: 0.5 },
        emphasis: { lineStyle: { width: 1 } },
        data: [{ xAxis: this.quadrant }, { yAxis: this.quadrant }],
      },
    };
  }


  normalizeSize(value: number, minSize = 10, maxSize = 60, data: any) {
    const allData = [
      ...(data?.q1 || []),
      ...(data?.q2 || []),
      ...(data?.q3 || []),
      ...(data?.q4 || []),
    ];
    const allRevenue = allData.map((item) => this.normalizeBubblePoint(item).value[2]);
    if (!allRevenue.length) return minSize;
    const minValue = Math.min(...allRevenue);
    const maxValue = Math.max(...allRevenue);

    // If all values are the same, return middle size
    if (maxValue === minValue) return (minSize + maxSize) / 2;

    // Determine raw linear size
    const rawSize =
      ((value - minValue) / (maxValue - minValue)) * (maxSize - minSize) +
      minSize;

    // Compress differences for  datasets <= 2
    const dataCount = allRevenue.length;
    const range = maxValue - minValue;
    if (dataCount <= 2 || range < 10) {
      const midSize = (minSize + maxSize) / 2;
      const diffFactor = 0.12;
      const diff = (maxSize - minSize) * diffFactor;
      return minValue === value ? midSize - diff : midSize + diff;
    }
    return rawSize;
  }

  onChartInit(chart: any) {
    if (this.data.updateOnclick) {
      chart.on('click', (params: any) => {
        if (params.seriesType === 'scatter') {
          const { seriesIndex, dataIndex } = params;

          if (this.selectedBubbleIndex !== null) {
            chart.dispatchAction({
              type: 'unselect',
              seriesIndex: this.selectedBubbleIndex,
              dataIndex: this.selectedDataIndex,
            });
          }

          chart.dispatchAction({ type: 'select', seriesIndex, dataIndex });
          this.selectedBubbleIndex = seriesIndex;
          this.selectedDataIndex = dataIndex;
          this.lastSelectedBubblePoint = { ...(params.data || {}) };
          if (this.data.generatedData || (this.data.drilldownUrl && this.data.drillDownFunctionName)) {
            this.setDrilldown(params.data.name, params.data);
          }
          this.service.setBubbleClicked(this.data.widgetId);
        }
      });
    }
  }
  setDrilldown(event: string, data?: any) {
    this.productId = event;
    this.service.setDrilldownTitle(event);
    const requestId = ++this.latestDrilldownRequestId;

    if (this.data.generatedData) {
      this.scenarioPayload = {
        ...(this.scenarioPayload ?? {}),
      };
      if (data?.scenarioKey !== undefined && data?.scenarioKey !== null && data?.scenarioKey !== '') {
        this.scenarioPayload.scenarioKey = data.scenarioKey;
      }
      this.service.customApi(this.data.drilldownUrl, this.scenarioPayload, this.data).subscribe((resp: any) => {
        if (requestId !== this.latestDrilldownRequestId) return;
        const data = this.extractDrilldownTree(resp);
        this.service.setDrilldownData({ data: data, widgetId: this.data.widgetId });
        this.cdr.detectChanges();
        this.bubbleClicked.emit();
      });
    }

    else {
      const filter = this.isHistoricalProductMetricsPage()
        ? this.buildHistoricalDrilldownFilter(data)
        : {
            "product_id": [event],
            "entity": "product_header"
          };

      this.service.fetchData(this.data.drilldownUrl, this.data.drillDownFunctionName, filter, this.data ?? {}).subscribe((res: any) => {
        if (requestId !== this.latestDrilldownRequestId) return;
        const drilldownTree = this.extractDrilldownTree(res);
        this.service.setDrilldownData({ data: drilldownTree, widgetId: this.data.widgetId });
        this.cdr.detectChanges();
        this.bubbleClicked.emit();
      });
    }

  }

  private buildHistoricalDrilldownFilter(point?: any) {
    const parsedFromName = this.parseHistoricalBubbleName(point?.name);
    const resolvedFinalEm = this.toFilterValues(
      point?.final_em ??
      point?.engine_model ??
      point?.engineModel ??
      point?.platform ??
      parsedFromName.finalEm ??
      this.filters?.final_em
    );
    const resolvedVertical = this.toFilterValues(
      point?.cleaned_vertical ??
      point?.market_segment ??
      point?.segment ??
      parsedFromName.cleanedVertical ??
      this.filters?.cleaned_vertical
    );
    const resolvedQuarterYear = this.toFilterValues(
      this.filters?.quarter_year ?? point?.quarter_year ?? point?.quarterYear
    );

    const filter: any = {};
    if (resolvedFinalEm.length) filter.final_em = resolvedFinalEm;
    if (resolvedVertical.length) filter.cleaned_vertical = resolvedVertical;
    if (resolvedQuarterYear.length) filter.quarter_year = resolvedQuarterYear;
    return filter;
  }

  private parseHistoricalBubbleName(name: any): { finalEm?: string; cleanedVertical?: string } {
    const raw = String(name ?? '').trim();
    if (!raw) return {};
    const parts = raw.split(/\s+/).filter(Boolean);
    if (!parts.length) return {};
    const finalEm = parts[0];
    const cleanedVertical = parts.length > 1 ? parts.slice(1).join(' ') : undefined;
    return { finalEm, cleanedVertical };
  }

  private toFilterValues(value: any): string[] {
    if (value === null || value === undefined) return [];
    const normalized = Array.isArray(value) ? value.flat(Infinity) : [value];
    return normalized
      .filter((v: any) => v !== null && v !== undefined && v !== '')
      .map((v: any) => String(v));
  }

  private extractDrilldownTree(res: any): any[] {
    const responseData = res?.responseList?.[0]?.responseData ?? res?.responseData;
    const candidates = [
      responseData?.[0]?.drillDownTree,
      responseData?.drillDownTree,
      res?.responseList?.[0]?.drillDownTree,
      res?.drillDownTree
    ];
    const resolved = candidates.find((item) => item !== undefined && item !== null);
    if (!resolved) return [];
    return Array.isArray(resolved) ? resolved : [resolved];
  }
  download() {
    this.service.downloadScores().subscribe((blob) => {
      const file = new Blob([blob], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(file);
      link.download = 'Raw Data.xlsx';
      link.click();
      URL.revokeObjectURL(link.href);
    });
  }
}
